import { Box, Button, Flex, Link, Text } from '@everlywell/ui-kit';
import { useCart } from 'components/Cart';
import React, {
  useReducer,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { PRODUCT_SLUGS } from 'utils/constants/products';
import useProductVariants from 'utils/hooks/useProductVariants';

import { EvaIcon } from '../../PageSections/DynamicQuizModal/BiomarkerInsights/EvaIcon';
import { ANIMATIONS } from '../animations';
import {
  useUpsell,
  UpsellRule,
  storeUpsell,
  removeAcceptedUpsell,
  getAcceptedUpsellFromLocalStorage,
  hasCartUpsellInLocalStorage,
} from '../useUpsell';
import { parseTextWithHighlights } from '../utils';

interface CartUpsellProps {
  isExpandedByDefault?: boolean;
}

type UpsellStatus =
  | 'initial'
  | 'expanded'
  | 'idle'
  | 'accepted'
  | 'declined';

interface AnimationState {
  initialView: boolean;
  undoLink: boolean;
  expandedText: boolean;
  buttons: boolean;
}

interface UIContext {
  isFromAccepted: boolean;
  shouldCenter: boolean;
}

interface CartUpsellState {
  status: UpsellStatus;
  animations: AnimationState;
  ui: UIContext;
}

type CartUpsellAction =
  | { type: 'SET_STATUS'; payload: UpsellStatus }
  | {
      type: 'SET_ANIMATION';
      payload: { key: keyof AnimationState; value: boolean };
    }
  | {
      type: 'SET_UI_CONTEXT';
      payload: { key: keyof UIContext; value: boolean };
    }
  | { type: 'RESET_ANIMATIONS' }
  | { type: 'RESET_UI_CONTEXT' }
  | { type: 'TRANSITION_TO_EXPANDED' }
  | { type: 'TRANSITION_TO_ACCEPTED' }
  | { type: 'TRANSITION_TO_DECLINED' };

const initialState: CartUpsellState = {
  status: 'initial',
  animations: {
    initialView: false,
    undoLink: false,
    expandedText: false,
    buttons: false,
  },
  ui: {
    isFromAccepted: false,
    shouldCenter: false,
  },
};

const cartUpsellReducer = (
  state: CartUpsellState,
  action: CartUpsellAction,
): CartUpsellState => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_ANIMATION':
      return {
        ...state,
        animations: {
          ...state.animations,
          [action.payload.key]: action.payload.value,
        },
      };
    case 'SET_UI_CONTEXT':
      return {
        ...state,
        ui: {
          ...state.ui,
          [action.payload.key]: action.payload.value,
        },
      };
    case 'RESET_ANIMATIONS':
      return {
        ...state,
        animations: {
          initialView: false,
          undoLink: false,
          expandedText: false,
          buttons: false,
        },
      };
    case 'RESET_UI_CONTEXT':
      return {
        ...state,
        ui: {
          // Preserve isFromAccepted when in expanded state, as it's needed for animation logic
          isFromAccepted:
            state.status === 'expanded' ? state.ui.isFromAccepted : false,
          shouldCenter: false,
        },
      };
    case 'TRANSITION_TO_EXPANDED':
      return {
        ...state,
        status: 'expanded',
        animations: {
          ...state.animations,
          initialView: false,
        },
      };
    case 'TRANSITION_TO_ACCEPTED':
      return {
        ...state,
        status: 'accepted',
        animations: {
          initialView: false,
          undoLink: false,
          expandedText: false,
          buttons: false,
        },
        ui: {
          isFromAccepted: false,
          shouldCenter: false,
        },
      };
    case 'TRANSITION_TO_DECLINED':
      return {
        ...state,
        status: 'declined',
        animations: {
          initialView: false,
          undoLink: false,
          expandedText: false,
          buttons: false,
        },
        ui: {
          isFromAccepted: false,
          shouldCenter: false,
        },
      };
    default:
      return state;
  }
};

const CartUpsell = ({ isExpandedByDefault = false }: CartUpsellProps) => {
  const [state, dispatch] = useReducer(cartUpsellReducer, initialState);
  const { order, addItemToCartAsync, removeProduct } = useCart();
  const productVariants = useProductVariants();
  // Freeze the rule when in expanded, idle, or accepted state to prevent recalculation during swap/add
  const { rule, shouldShow } = useUpsell({
    preferCartSidebar: true,
    freezeRule:
      (!isExpandedByDefault && state.status === 'expanded') ||
      state.status === 'idle' ||
      state.status === 'accepted' ||
      state.status === 'declined',
  });

  // Store the rule and action details for undo functionality
  // This allows us to show accepted state and undo even after rule disappears
  const acceptedStateRef = useRef<{
    rule: UpsellRule | null;
    acceptedMessage: string;
    removedLineItems: Array<{
      lineItemId: number;
      variantId: number;
      quantity: number;
    }>;
    addedVariantIds: Array<number>;
    /** 'dismiss' = user chose No thanks (no cart changes); 'accept' = swap/add applied */
    interactionKind?: 'accept' | 'dismiss';
  } | null>(null);

  // Refs to store timeout IDs for cleanup
  const viewButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const swapButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const undoLinkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasRestoredFromLocalStorageRef = useRef<boolean>(false);
  /** True only if cart upsell existed in localStorage when this instance mounted (session restore). */
  const hadStoredUpsellOnMountRef = useRef(false);
  useLayoutEffect(() => {
    hadStoredUpsellOnMountRef.current = hasCartUpsellInLocalStorage();
  }, []);
  const hasTrackedShownRef = useRef<boolean>(false);
  const [shouldHideOnFirstRender, setShouldHideOnFirstRender] =
    React.useState(false);

  // Refs for dynamic height measurement
  const containerRef = useRef<HTMLDivElement>(null);
  const expandedContentRef = useRef<HTMLDivElement>(null);
  const acceptedIdleContentRef = useRef<HTMLDivElement>(null);
  const isFirstMeasurementRef = useRef<boolean>(true);
  const previousStatusRef = useRef<UpsellStatus>(state.status);
  const previousRuleIdRef = useRef<string | undefined>(rule?.id);
  const [dynamicHeight, setDynamicHeight] = React.useState<number>(199);

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (viewButtonTimeoutRef.current) {
        clearTimeout(viewButtonTimeoutRef.current);
      }
      if (swapButtonTimeoutRef.current) {
        clearTimeout(swapButtonTimeoutRef.current);
      }
      if (undoLinkTimeoutRef.current) {
        clearTimeout(undoLinkTimeoutRef.current);
      }
    },
    [],
  );

  // Set status to expanded if isExpandedByDefault is true
  useEffect(() => {
    if (isExpandedByDefault && state.status === 'initial') {
      dispatch({ type: 'TRANSITION_TO_EXPANDED' });
    }
  }, [isExpandedByDefault, state.status]);

  // Reset expanded state flags when not in expanded state
  useEffect(() => {
    dispatch({ type: 'RESET_ANIMATIONS' });
    dispatch({ type: 'RESET_UI_CONTEXT' });
  }, [state.status]);

  // Handle delayed centering for accepted / declined confirmation row
  useEffect(() => {
    if (state.status === 'accepted' || state.status === 'declined') {
      const centerTimer = setTimeout(() => {
        dispatch({
          type: 'SET_UI_CONTEXT',
          payload: { key: 'shouldCenter', value: true },
        });
      }, 500);

      return () => clearTimeout(centerTimer);
    } else {
      dispatch({
        type: 'SET_UI_CONTEXT',
        payload: { key: 'shouldCenter', value: false },
      });
    }
  }, [state.status]);

  // Reset status to initial when rule changes
  useEffect(() => {
    const currentRuleId = rule?.id;
    const previousRuleId = previousRuleIdRef.current;

    // If rule changed (different ID or rule disappeared/appeared)
    const ruleChanged = currentRuleId !== previousRuleId;

    if (
      ruleChanged &&
      state.status !== 'accepted' &&
      state.status !== 'idle' &&
      state.status !== 'expanded' &&
      state.status !== 'declined'
    ) {
      dispatch({ type: 'SET_STATUS', payload: 'initial' });
    }

    // Reset shown tracking when rule changes
    if (ruleChanged) {
      hasTrackedShownRef.current = false;
    }

    // Update previous rule ID
    previousRuleIdRef.current = currentRuleId;
  }, [rule?.id, state.status]);

  // Track when upsell is first shown
  useEffect(() => {
    if (
      rule &&
      shouldShow &&
      state.status === 'initial' &&
      !hasTrackedShownRef.current
    ) {
      hasTrackedShownRef.current = true;
      // Sidebar - Tracking - Shown
      analytics.track({
        event: ANALYTICS.EVENTS.SIDEBAR_UPSELL_SHOWN,
        data: {
          product_id: rule?.triggerLineItem.product_id,
          copy: rule?.copy.initialMessage,
          order_id: order?.id,
          recommended_product_id: rule?.action.addProducts[0],
          upsell_type: rule?.action.type,
          recommendation_priority: rule?.step,
          test_variant: 'sidebar_only',
          cart_contents: order?.line_items?.map((item) => ({
            product_id: item.product_id,
            price: parseFloat(item.base_price ?? '0'),
            quantity: item.quantity,
          })),
        },
      });
    }
  }, [rule, shouldShow, state.status, order]);

  // idle is a brief transition frame before accepted
  useEffect(() => {
    if (state.status === 'idle') {
      dispatch({ type: 'SET_STATUS', payload: 'accepted' });
    }
  }, [state.status]);

  // Determine which rule/message to use
  // Use stored rule if in accepted/idle state, otherwise use current rule
  // When in accepted/idle state, always use the stored rule to prevent showing a different rule after swap
  const activeRule =
    state.status === 'accepted' ||
    state.status === 'idle' ||
    state.status === 'declined'
      ? acceptedStateRef.current?.rule ?? null
      : rule;

  const confirmationMessage =
    state.status === 'accepted' || state.status === 'idle'
      ? acceptedStateRef.current?.acceptedMessage || rule?.copy.acceptedMessage
      : state.status === 'declined'
      ? acceptedStateRef.current?.acceptedMessage ||
        rule?.copy.declinedMessage
      : undefined;

  // Parse expanded message with highlights
  const expandedMessageSegments = useMemo(() => {
    const messageToUse = activeRule?.copy.expandedMessageWithMarkdown;
    if (!messageToUse) return [];
    return parseTextWithHighlights(messageToUse);
  }, [activeRule?.copy.expandedMessageWithMarkdown]);

  // Reset first measurement flag when transitioning from expanded/idle to other states
  useEffect(() => {
    if (
      (previousStatusRef.current === 'expanded' ||
        previousStatusRef.current === 'idle') &&
      state.status !== 'expanded' &&
      state.status !== 'idle'
    ) {
      isFirstMeasurementRef.current = true;
    }
  }, [state.status]);

  // Measure and update dynamic heights when content changes
  useEffect(() => {
    // Capture previous status before updating it
    const previousStatus = previousStatusRef.current;
    const isAcceptedToExpanded =
      (previousStatus === 'accepted' || previousStatus === 'declined') &&
      state.status === 'expanded';

    // Update previousStatusRef at the end of this effect, after we've used it
    previousStatusRef.current = state.status;

    const measureHeight = () => {
      // Use 32px padding when transitioning from accepted to expanded, otherwise 16px
      const padding = isAcceptedToExpanded ? 32 : 16;

      if (isFirstMeasurementRef.current) {
        isFirstMeasurementRef.current = false;
      }

      if (state.status === 'expanded' && expandedContentRef.current) {
        const height = expandedContentRef.current.scrollHeight;
        setDynamicHeight(height + padding);
      } else if (state.status === 'idle' && acceptedIdleContentRef.current) {
        const height = acceptedIdleContentRef.current.scrollHeight;
        setDynamicHeight(height + padding);
      }
    };

    requestAnimationFrame(() => {
      measureHeight();
    });
  }, [state.status, expandedMessageSegments, activeRule]);

  // Recalculate height on window resize
  useEffect(() => {
    const handleResize = () => {
      // Capture previous status before it gets updated by other effects
      const previousStatus = previousStatusRef.current;
      const isAcceptedToExpanded =
        (previousStatus === 'accepted' || previousStatus === 'declined') &&
        state.status === 'expanded';

      const measureHeight = () => {
        // Use 32px padding when transitioning from accepted to expanded, otherwise 16px
        const padding = isAcceptedToExpanded ? 32 : 16;

        if (isFirstMeasurementRef.current) {
          isFirstMeasurementRef.current = false;
        }

        if (state.status === 'expanded' && expandedContentRef.current) {
          const height = expandedContentRef.current.scrollHeight;
          setDynamicHeight(height + padding);
        } else if (state.status === 'idle' && acceptedIdleContentRef.current) {
          const height = acceptedIdleContentRef.current.scrollHeight;
          setDynamicHeight(height + padding);
        }
      };

      requestAnimationFrame(() => {
        measureHeight();
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [state.status]);

  // Handle swap/add action
  const handleSwapOrAdd = async () => {
    if (!rule) return;

    // Clear any existing timeout
    if (swapButtonTimeoutRef.current) {
      clearTimeout(swapButtonTimeoutRef.current);
    }

    // Trigger fade-out animations for text, buttons, and disclaimer
    dispatch({
      type: 'SET_ANIMATION',
      payload: { key: 'expandedText', value: true },
    });
    dispatch({
      type: 'SET_ANIMATION',
      payload: { key: 'buttons', value: true },
    });

    // Track removed line items for undo
    const removedLineItems: Array<{
      lineItemId: number;
      variantId: number;
      quantity: number;
    }> = [];

    // Handle REPLACE action: remove old products, add new ones
    if (rule.action.type === 'REPLACE' && rule.action.removeProducts) {
      // Remove products first and track them for undo
      const lineItemsToRemove = order?.line_items.filter((item) =>
        rule.action.removeProducts?.includes(item.product_id || 0),
      );
      lineItemsToRemove?.forEach((item) => {
        if (item.id && item.variant_id !== undefined) {
          removedLineItems.push({
            lineItemId: item.id,
            variantId: item.variant_id,
            quantity: item.quantity || 1,
          });
          removeProduct(item.id);
        }
      });
    }

    // Track added variant IDs for undo
    const addedVariantIds: number[] = [];

    // Add new products
    const addPromises = rule.action.addProducts
      .map((productId) => {
        // Ensure productId is a number for consistent lookup
        const productIdNum = Number(productId);
        // Try both number and string keys since productVariants keys might be strings
        const productVariant =
          productVariants[productIdNum] ||
          (productVariants as Record<string, (typeof productVariants)[number]>)[
            String(productIdNum)
          ];

        if (productVariant?.variantId) {
          let variantId = productVariant.variantId;

          if (productVariant.sku === PRODUCT_SLUGS.EVERLYWELL_360_FEMALE) {
            const ew360Variant = productVariant.subscriptionVariants.find(
              (variant) =>
                variant.sku === '360-full-body-test-female-member-annual',
            );

            variantId = ew360Variant
              ? ew360Variant.id
              : productVariant.variantId;
          }

          if (productVariant.sku === PRODUCT_SLUGS.EVERLYWELL_360_MALE) {
            const ew360Variant = productVariant.subscriptionVariants.find(
              (variant) =>
                variant.sku === '360-full-body-test-male-member-annual',
            );

            variantId = ew360Variant
              ? ew360Variant.id
              : productVariant.variantId;
          }

          addedVariantIds.push(variantId);

          if (!order?.id) return null;

          return addItemToCartAsync({
            variant_id: variantId,
            quantity: 1,
          }).catch((error) => {
            throw error;
          });
        }
        return null;
      })
      .filter((promise) => promise !== null);

    // Wait for all products to be added
    try {
      await Promise.all(addPromises);

      // Update with the actual removed/added items after operations complete
      acceptedStateRef.current = {
        rule,
        acceptedMessage: rule.copy.acceptedMessage,
        removedLineItems,
        addedVariantIds,
      };

      // Store accepted upsell in localStorage immediately after cart operations complete
      if (order?.id) {
        storeUpsell(
          {
            rule,
            acceptedMessage: rule.copy.acceptedMessage,
            removedLineItems,
            addedVariantIds,
          },
          'cart-sidebar',
          'accepted',
          order.id,
        );
      }

      // Sidebar - Tracking - Cart Updated
      if (rule && shouldShow && order?.id) {
        analytics.track({
          event: ANALYTICS.EVENTS.SIDEBAR_CART_UPDATED,
          data: {
            product_id: rule?.triggerLineItem.product_id,
            order_id: order.id,
            recommended_product_id: rule?.action.addProducts[0],
            upsell_type: rule?.action.type,
            recommendation_priority: rule?.step,
            test_variant: 'sidebar_only',
            cart_contents: order?.line_items?.map((item) => ({
              product_id: item.product_id,
              price: parseFloat(item.base_price ?? '0'),
              quantity: item.quantity,
            })),
          },
        });
      }
    } catch (error) {
      // Keep the rule stored even if operations fail
      analytics.track({
        event: ANALYTICS.EVENTS.CART_UPDATE_ERROR,
        data: {
          product_id: rule?.triggerLineItem.product_id,
          order_id: order?.id,
          recommended_product_id: rule?.action.addProducts[0],
          upsell_type: rule?.action.type,
          recommendation_priority: rule?.step,
          test_variant: 'sidebar_only',
          cart_contents: order?.line_items?.map((item) => ({
            product_id: item.product_id,
            price: parseFloat(item.base_price ?? '0'),
            quantity: item.quantity,
          })),
        },
      });
    }

    swapButtonTimeoutRef.current = setTimeout(
      () => dispatch({ type: 'SET_STATUS', payload: 'idle' }),
      100,
    );
  };

  // Handle undo action — reverse swap/add, or clear a dismiss and return to expanded
  const handleUndo = async () => {
    if (!acceptedStateRef.current) return;

    const { removedLineItems, addedVariantIds, interactionKind } =
      acceptedStateRef.current;

    // Clear any existing timeout
    if (undoLinkTimeoutRef.current) {
      clearTimeout(undoLinkTimeoutRef.current);
    }

    if (interactionKind === 'dismiss') {
      removeAcceptedUpsell('cart-sidebar');
      acceptedStateRef.current = null;
      dispatch({
        type: 'SET_ANIMATION',
        payload: { key: 'undoLink', value: true },
      });
      dispatch({
        type: 'SET_UI_CONTEXT',
        payload: { key: 'isFromAccepted', value: true },
      });
      undoLinkTimeoutRef.current = setTimeout(
        () => dispatch({ type: 'TRANSITION_TO_EXPANDED' }),
        500,
      ) as any;
      return;
    }

    // Remove the items that were added
    if (order?.line_items && addedVariantIds.length > 0) {
      const lineItemsToRemove = order.line_items.filter(
        (item) =>
          item.variant_id !== undefined &&
          addedVariantIds.includes(item.variant_id),
      );
      lineItemsToRemove.forEach((item) => {
        if (item.id) {
          removeProduct(item.id);
        }
      });
    }

    // Add back the items that were removed (for REPLACE actions)
    if (removedLineItems.length > 0 && order?.id) {
      const addBackPromises = removedLineItems.map(({ variantId, quantity }) =>
        addItemToCartAsync({
          variant_id: variantId,
          quantity,
        }).catch((error) => {
          throw error;
        }),
      );

      try {
        await Promise.all(addBackPromises);
      } catch (error) {}
    }

    // Remove stored accepted upsell from localStorage
    removeAcceptedUpsell('cart-sidebar');

    // Clear the accepted state and transition back to expanded
    acceptedStateRef.current = null;
    dispatch({
      type: 'SET_ANIMATION',
      payload: { key: 'undoLink', value: true },
    });
    dispatch({
      type: 'SET_UI_CONTEXT',
      payload: { key: 'isFromAccepted', value: true },
    });
    undoLinkTimeoutRef.current = setTimeout(
      () => dispatch({ type: 'TRANSITION_TO_EXPANDED' }),
      500,
    ) as any;
  };

  // Check localStorage and restore accepted status if cart upsell exists
  useEffect(() => {
    const storedData = getAcceptedUpsellFromLocalStorage('cart-sidebar');
    if (storedData) {
      // Always update acceptedStateRef with stored data first, prioritizing stored acceptedMessage
      const messageToUse =
        storedData.acceptedMessage ||
        rule?.copy.acceptedMessage ||
        "Great choice! I've updated your cart.";

      const restoredDismissed = storedData.status === 'dismissed';

      if (rule) {
        acceptedStateRef.current = {
          rule,
          acceptedMessage: messageToUse,
          removedLineItems: storedData.removedLineItems,
          addedVariantIds: storedData.addedVariantIds,
          interactionKind: restoredDismissed ? 'dismiss' : 'accept',
        };
      } else {
        // If no rule yet, still set the message from localStorage
        acceptedStateRef.current = {
          rule: null,
          acceptedMessage: messageToUse,
          removedLineItems: storedData.removedLineItems,
          addedVariantIds: storedData.addedVariantIds,
          interactionKind: restoredDismissed ? 'dismiss' : 'accept',
        };
      }

      // Restore persisted state once; hide widget only if storage existed on mount (refresh), not after first write in-session
      if (!hasRestoredFromLocalStorageRef.current) {
        hasRestoredFromLocalStorageRef.current = true;
        if (hadStoredUpsellOnMountRef.current) {
          setShouldHideOnFirstRender(true);
        }
        dispatch({
          type: 'SET_STATUS',
          payload: restoredDismissed ? 'declined' : 'accepted',
        });
      }
    }
  }, [rule]);

  // Hide when a persisted cart upsell was restored on load (see restore effect)
  const hasRule = rule !== undefined || acceptedStateRef.current !== null;
  const shouldRenderComponent = hasRule && !shouldHideOnFirstRender;

  if (!shouldRenderComponent) return null;

  return (
    <>
      <Box
        ref={containerRef}
        padding={4}
        backgroundColor="eggplant.wash"
        borderRadius="8px"
        sx={{
          transition:
            'height 500ms cubic-bezier(0.4, 0, 0.2, 1), place-content 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          height:
            state.status === 'initial' ||
            state.status === 'accepted' ||
            state.status === 'declined'
              ? '72px'
              : `${dynamicHeight}px`,
          placeContent: state.ui.shouldCenter ? 'center' : 'initial',
          animation: ANIMATIONS.container.fadeIn.animation,
          ...ANIMATIONS.container.fadeIn.keyframes,
        }}
      >
        <Box
          ref={state.status === 'idle' ? acceptedIdleContentRef : undefined}
          display="flex"
          flexDirection={state.status === 'expanded' ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          {...(state.status === 'expanded' && { height: '100%' })}
        >
          <Box
            display="flex"
            flexDirection="row"
            gap={4}
            alignItems="center"
            height="100%"
          >
            <Box
              backgroundColor="violet.lighter"
              borderRadius="full"
              padding={2}
              display="flex"
              alignItems="center"
              justifyContent="left"
              placeSelf="flex-start"
              sx={{
                animation: ANIMATIONS.evaIcon.slideInBounce.cartAnimation,
                ...ANIMATIONS.evaIcon.slideInBounce.keyframes,
              }}
            >
              <EvaIcon />
            </Box>

            {(state.status === 'initial' ||
              state.status === 'accepted' ||
              state.status === 'idle' ||
              state.status === 'declined') && (
              <Text
                color="tints.darkGrey"
                size="lg"
                sx={{
                  animation: state.animations.initialView
                    ? ANIMATIONS.text.fadeOut.animation
                    : state.animations.undoLink &&
                      (state.status === 'accepted' ||
                        state.status === 'idle' ||
                        state.status === 'declined')
                    ? ANIMATIONS.link.fadeOut.animation
                    : state.status === 'accepted' ||
                      state.status === 'idle' ||
                      state.status === 'declined'
                    ? ANIMATIONS.text.fadeInAccepted.animation
                    : ANIMATIONS.text.fadeIn.animation,
                  ...(state.animations.initialView
                    ? ANIMATIONS.text.fadeOut.keyframes
                    : state.animations.undoLink &&
                      (state.status === 'accepted' ||
                        state.status === 'idle' ||
                        state.status === 'declined')
                    ? ANIMATIONS.link.fadeOut.keyframes
                    : state.status === 'accepted' ||
                      state.status === 'idle' ||
                      state.status === 'declined'
                    ? ANIMATIONS.text.fadeInAccepted.keyframes
                    : ANIMATIONS.text.fadeIn.keyframes),
                }}
              >
                {state.status === 'initial' && activeRule?.copy.initialMessage}
                {(state.status === 'accepted' ||
                  state.status === 'idle' ||
                  state.status === 'declined') &&
                  confirmationMessage}
              </Text>
            )}
            {state.status === 'expanded' && (
              <Box
                ref={expandedContentRef}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="100%"
                sx={{
                  animation:
                    state.status === 'expanded'
                      ? ANIMATIONS.expandContent.expand.animation
                      : 'none',
                  ...(state.status === 'expanded'
                    ? ANIMATIONS.expandContent.expand.keyframes
                    : {}),
                }}
              >
                <Text
                  color="tints.darkGrey"
                  size="lg"
                  sx={{
                    animation: state.animations.expandedText
                      ? ANIMATIONS.global.fadeOut.animation
                      : ANIMATIONS.text.expandedFadeIn.animation,
                    ...(state.animations.expandedText
                      ? ANIMATIONS.global.fadeOut.keyframes
                      : ANIMATIONS.text.expandedFadeIn.keyframes),
                  }}
                >
                  {expandedMessageSegments.map((segment, index) =>
                    segment.isHighlight ? (
                      <Box
                        key={index}
                        fontWeight="500"
                        as="span"
                        backgroundColor="violet.lighter"
                        color="tints.black"
                        sx={{
                          position: 'relative',
                          animation:
                            ANIMATIONS.backgroundHighlight.fadeIn.animation,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '1em',
                            backgroundColor: 'violet.lighter',
                            zIndex: -1,
                            // When transitioning from accepted to expanded, use fadeIn instead of growFromLeft
                            ...(state.ui.isFromAccepted
                              ? {
                                  animation:
                                    ANIMATIONS.backgroundHighlight.fadeIn
                                      .animation,
                                  ...ANIMATIONS.backgroundHighlight.fadeIn
                                    .keyframes,
                                }
                              : {
                                  animation:
                                    ANIMATIONS.backgroundHighlight.growFromLeft
                                      .animation,
                                  ...ANIMATIONS.backgroundHighlight.growFromLeft
                                    .keyframes,
                                }),
                          },
                          ...ANIMATIONS.backgroundHighlight.fadeIn.keyframes,
                        }}
                      >
                        {segment.text}
                      </Box>
                    ) : (
                      <React.Fragment key={index}>
                        {segment.text}
                      </React.Fragment>
                    ),
                  )}
                </Text>

                <Box>
                  <Flex
                    flexDirection="row"
                    marginTop={4}
                    gap={2}
                    sx={{
                      animation: state.animations.buttons
                        ? ANIMATIONS.global.fadeOut.animation
                        : state.status === 'expanded'
                        ? state.ui.isFromAccepted
                          ? ANIMATIONS.buttonsContainer.fadeInOnly.animation
                          : ANIMATIONS.buttonsContainer.fadeIn.animation
                        : 'none',
                      ...(state.animations.buttons
                        ? ANIMATIONS.global.fadeOut.keyframes
                        : state.status === 'expanded'
                        ? state.ui.isFromAccepted
                          ? ANIMATIONS.buttonsContainer.fadeInOnly.keyframes
                          : ANIMATIONS.buttonsContainer.fadeIn.keyframes
                        : {}),
                    }}
                  >
                    <Button
                      variant="secondary"
                      height="40px"
                      borderColor="eggplant.base"
                      boxShadow="inset 0 0 0 2px rgb(107,46,113)"
                      _hover={{
                        boxShadow: 'inset 0 0 0 2px rgb(107,46,113)',
                        borderColor: 'eggplant.base',
                        filter: 'brightness(0.95)',
                      }}
                      width="100%"
                      onClick={() => {
                        if (rule) {
                          acceptedStateRef.current = {
                            rule,
                            acceptedMessage: rule.copy.declinedMessage,
                            removedLineItems: [],
                            addedVariantIds: [],
                            interactionKind: 'dismiss',
                          };
                          dispatch({ type: 'TRANSITION_TO_DECLINED' });
                        }

                        if (rule && shouldShow && order?.id) {
                          // Store dismissed upsell data
                          storeUpsell(
                            {
                              rule,
                              acceptedMessage: rule.copy.declinedMessage,
                              removedLineItems: [],
                              addedVariantIds: [],
                            },
                            'cart-sidebar',
                            'dismissed',
                            order.id,
                          );

                          // Sidebar - Tracking - Rejected
                          analytics.track({
                            event: ANALYTICS.EVENTS.SIDEBAR_UPSELL_DISMISSED,
                            data: {
                              product_id: rule?.triggerLineItem.product_id,
                              order_id: order.id,
                              recommended_product_id:
                                rule?.action.addProducts[0],
                              upsell_type: rule?.action.type,
                              recommendation_priority: rule?.step,
                              test_variant: 'sidebar_only',
                              price_delta: rule?.action.priceDelta,
                              dismiss_method: 'No thanks',
                              cart_contents: order?.line_items?.map((item) => ({
                                product_id: item.product_id,
                                price: parseFloat(item.base_price ?? '0'),
                                quantity: item.quantity,
                              })),
                            },
                          });
                        }
                      }}
                      sx={{
                        transition: 'all 200ms ease-out',
                      }}
                    >
                      <Text fontSize="lg" color="eggplant.base">
                        No, thanks
                      </Text>
                    </Button>
                    <Button
                      height="40px"
                      backgroundColor="eggplant.base"
                      _hover={{
                        backgroundColor: 'eggplant.base',
                        filter: 'brightness(0.95)',
                      }}
                      width="100%"
                      onClick={() => {
                        handleSwapOrAdd();

                        if (rule && shouldShow) {
                          // Sidebar - Tracking - Accepted
                          analytics.track({
                            event: ANALYTICS.EVENTS.SIDEBAR_UPSELL_ACCEPTED,
                            data: {
                              product_id: rule?.triggerLineItem.product_id,
                              order_id: order?.id,
                              recommended_product_id:
                                rule?.action.addProducts[0],
                              upsell_type: rule?.action.type,
                              recommendation_priority: rule?.step,
                              test_variant: 'sidebar_only',
                              price_delta: rule?.action.priceDelta,
                              cart_contents: order?.line_items?.map((item) => ({
                                product_id: item.product_id,
                                price: parseFloat(item.base_price ?? '0'),
                                quantity: item.quantity,
                              })),
                            },
                          });
                        }
                      }}
                      sx={{
                        transition: 'all 200ms ease-out',
                      }}
                    >
                      <Text fontSize="lg">{activeRule?.copy.buttonText}</Text>
                    </Button>
                  </Flex>

                  <Text
                    fontSize="xs"
                    color="tints.darkGrey"
                    marginTop={4}
                    textAlign="center"
                    sx={{
                      animation: state.animations.buttons
                        ? ANIMATIONS.global.fadeOut.animation
                        : state.status === 'expanded'
                        ? state.ui.isFromAccepted
                          ? ANIMATIONS.buttonsContainer.fadeInOnly.animation
                          : ANIMATIONS.buttonsContainer.fadeIn.animation
                        : 'none',
                      ...(state.animations.buttons
                        ? ANIMATIONS.global.fadeOut.keyframes
                        : state.status === 'expanded'
                        ? state.ui.isFromAccepted
                          ? ANIMATIONS.buttonsContainer.fadeInOnly.keyframes
                          : ANIMATIONS.buttonsContainer.fadeIn.keyframes
                        : {}),
                    }}
                  >
                    {activeRule?.copy.disclaimerText ||
                      '* Price does not include taxes and fees'}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>

          {state.status === 'initial' && (
            <Button
              height="40px"
              backgroundColor="eggplant.base"
              _hover={{
                backgroundColor: 'eggplant.base',
                filter: 'brightness(0.95)',
              }}
              onClick={() => {
                // Clear any existing timeout
                if (viewButtonTimeoutRef.current) {
                  clearTimeout(viewButtonTimeoutRef.current);
                }
                dispatch({
                  type: 'SET_ANIMATION',
                  payload: { key: 'initialView', value: true },
                });
                viewButtonTimeoutRef.current = setTimeout(
                  () => dispatch({ type: 'TRANSITION_TO_EXPANDED' }),
                  500,
                );

                if (rule && shouldShow) {
                  // Sidebar - Tracking - View
                  analytics.track({
                    event: ANALYTICS.EVENTS.SIDEBAR_UPSELL_EXPANDED,
                    data: {
                      product_id: rule?.triggerLineItem.product_id,
                      copy: rule?.copy.expandedMessage,
                      order_id: order?.id,
                      recommended_product_id: rule?.action.addProducts[0],
                      upsell_type: rule?.action.type,
                      recommendation_priority: rule?.step,
                      test_variant: 'sidebar_only',
                      cart_contents: order?.line_items?.map((item) => ({
                        product_id: item.product_id,
                        price: parseFloat(item.base_price ?? '0'),
                        quantity: item.quantity,
                      })),
                    },
                  });
                }
              }}
              sx={{
                animation: state.animations.initialView
                  ? ANIMATIONS.button.fadeOut.animation
                  : ANIMATIONS.button.fadeIn.animation,
                ...(state.animations.initialView
                  ? ANIMATIONS.button.fadeOut.keyframes
                  : ANIMATIONS.button.fadeIn.keyframes),
              }}
            >
              <Text fontSize="lg">View</Text>
            </Button>
          )}

          {(state.status === 'accepted' ||
            state.status === 'idle' ||
            state.status === 'declined') &&
            acceptedStateRef.current && (
              <Link
                onClick={handleUndo}
                color="eggplant.base"
                sx={{
                  animation: state.animations.undoLink
                    ? ANIMATIONS.link.fadeOut.animation
                    : ANIMATIONS.link.fadeIn.animation,
                  transition: 'all 200ms ease-out',
                  '&:hover': {},
                  ...(state.animations.undoLink
                    ? ANIMATIONS.link.fadeOut.keyframes
                    : ANIMATIONS.link.fadeIn.keyframes),
                }}
              >
                Undo
              </Link>
            )}
        </Box>
      </Box>
    </>
  );
};

export default CartUpsell;
